import { useMemo, useState, type FormEvent } from 'react';
import { Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import spinnerTitle from 'components/others/spinnerTitle';
import PriceAndPriceSale from 'components/others/PriceAndPriceSale';
import SvgSave from 'components/svg/SvgSave';
import type { SetEditFormValues } from '../types';
import SetFormGeneral from './tabs/SetFormGeneral';
import SetFormItems from './tabs/SetFormItems';
import SolarIcon from 'components/icons/SolarIcon';

interface SetEditFormProps {
    initialValues: SetEditFormValues;
    loading?: boolean;
    submitLabel: string;
    isCreate?: boolean;
    onSubmit: (payload: SetEditFormValues) => Promise<void>;
}

const SetEditForm = ({ initialValues, loading = false, submitLabel, isCreate = false, onSubmit }: SetEditFormProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Sets', 'common']);
    const { showToast } = useToast();
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const [activeTab, setActiveTab] = useState('general');
    const [formData, setFormData] = useState<SetEditFormValues>({
        setArticle: initialValues.setArticle || '',
        setName: initialValues.setName || '',
        setDescription: initialValues.setDescription || '',
        categoryId: Number(initialValues.categoryId || 0),
        items: initialValues.items?.length ? initialValues.items : [],
    });

    const totals = useMemo(() => {
        return formData.items.reduce(
            (acc, item) => {
                const count = Number(item.itemCount) || 0;
                acc.price += (Number(item.defPrice) || 0) * count;
                acc.priceSale += (Number(item.defPriceSale) || 0) * count;
                return acc;
            },
            { price: 0, priceSale: 0 },
        );
    }, [formData.items]);

    const handleChange = (patch: Partial<SetEditFormValues>) => {
        setFormData((prev) => ({ ...prev, ...patch }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const article = (formData.setArticle || '').trim();
        const name = (formData.setName || '').trim();

        if (!article || !name) {
            if (activeTab === 'products') setActiveTab('general');
            showToast(!article ? t('messEmptyArticle') : t('messEmptyName'), 'danger');
            return;
        }

        await onSubmit({
            ...formData,
            setArticle: article,
            setName: name,
        });
    };

    return (
        <Form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
            <div className="off-set-edit-tabs d-flex flex-column h-100">
                <div className="off-set-edit-tabs-content px-3">
                    <Tabs
                        variant="underline"
                        activeKey={activeTab}
                        onSelect={(tab) => tab && setActiveTab(tab)}
                        className="mb-3 invoice-tab border-bottom"
                    >
                        <Tab
                            eventKey="general"
                            title={
                                <>
                                    <SolarIcon name="gear" className="me-2 fs-14" />
                                    {t('edit.tabGeneral')}
                                </>
                            }
                        >
                            <SetFormGeneral
                                formData={formData}
                                onChange={handleChange}
                                disabled={!canEdit}
                                isCreate={isCreate}
                            />
                        </Tab>

                        <Tab
                            eventKey="products"
                            title={
                                <>
                                    <SolarIcon name="box-seam" className="me-2 fs-14" />
                                    {t('edit.tabProducts')}
                                </>
                            }
                        >
                            <SetFormItems formData={formData} onChange={handleChange} disabled={!canEdit} />
                        </Tab>
                    </Tabs>
                </div>

                <div className="p-3 offcanvas-footer off-set-edit-tabs-footer">
                    <div className="d-flex align-items-end justify-content-between gap-3">
                        <div className="off-set-edit-footer-price text-start text-truncate">
                            <div className="f-12 text-muted mb-1">{t('fields.setPriceList')}</div>
                            <PriceAndPriceSale price={totals.price} priceSale={totals.priceSale} />
                        </div>
                        {canEdit ? (
                            <Button type="submit" variant="info" disabled={loading} className="min100px flex-shrink-0">
                                {spinnerTitle(
                                    <>
                                        <SvgSave className="me-2 mtop-2" />
                                        {submitLabel}
                                    </>,
                                    loading,
                                    t('common:buttons.saving'),
                                )}
                            </Button>
                        ) : (
                            <div className="alert alert-warning text-start mb-0" role="alert">
                                {t('common:messReadOnly')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default SetEditForm;
